import React, { useCallback, useEffect, useState } from "react";
import { ArrowClockwise } from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GaSetup, GaSteps, GaTagStatus } from "@/components/ga/GaSetup";
import { GaDailyChart, GaTotals } from "@/components/ga/GaOverview";
import { GaTables } from "@/components/ga/GaTables";

const PILL = "border-2 border-black px-3 py-1 text-[10px] uppercase tracking-widest transition-colors duration-100";
const pill = (on) => `${PILL} ${on ? "bg-black text-white" : "bg-white hover:bg-zinc-100"}`;

function Controls({ days, setDays, scope, setScope, hosts, onRefresh, busy }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="ga-controls">
      {[7, 28, 90].map((d) => <button key={d} type="button" onClick={() => setDays(d)} className={pill(d === days)} data-testid={`ga-days-${d}`}>Last {d} days</button>)}
      {hosts.length > 0 && (
        <span className="inline-flex gap-1">
          <button type="button" onClick={() => setScope("landing")} className={pill(scope === "landing")} data-testid="ga-scope-landing">{hosts[0]} only</button>
          <button type="button" onClick={() => setScope("all")} className={pill(scope === "all")} data-testid="ga-scope-all">All sites</button>
        </span>
      )}
      <button type="button" onClick={onRefresh} disabled={busy} className={`${pill(false)} inline-flex items-center gap-1 disabled:opacity-50`} data-testid="ga-refresh">
        <ArrowClockwise size={11} weight="bold" /> {busy ? "Loading…" : "Refresh"}
      </button>
    </div>
  );
}

function GaBody() {
  const [days, setDays] = useState(28);
  const [scope, setScope] = useState("landing");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = useCallback((refresh = false) => {
    setBusy(true);
    api.get("/admin/google-analytics", { params: { days, scope, refresh } })
      .then((r) => setData(r.data))
      .catch((e) => setData({ failed: e?.response?.data?.detail || "Couldn't load Google Analytics." }))
      .finally(() => setBusy(false));
  }, [days, scope]);
  useEffect(() => { load(); }, [load]);
  const ready = data && data.configured && !data.error && data.totals;
  return (
    <div className="space-y-5" data-testid="ga-page">
      <div>
        <div className="label-mono">Report</div>
        <h1 className="font-chivo text-4xl font-black uppercase tracking-tighter sm:text-5xl">Google Analytics</h1>
        <p className="mt-2 max-w-3xl text-sm text-zinc-700">
          Visitors to your landing pages from your GA4 property: daily visits, top pages, where traffic comes from, how many reach
          the thank-you page, and visitors by state and device. Google&apos;s numbers can lag a few hours.
        </p>
      </div>
      <Controls days={days} setDays={setDays} scope={scope} setScope={setScope} hosts={data?.lander_hosts || []} onRefresh={() => load(true)} busy={busy} />
      {!data && <div className="label-mono">Loading…</div>}
      {data?.failed && <div className="text-sm text-rose-700" data-testid="ga-failed">{data.failed}</div>}
      {data && data.configured === false && <GaSetup data={data} />}
      {data?.error && (
        <div className="border-2 border-rose-700 bg-rose-50 p-4 text-sm" data-testid="ga-error">
          <b>Google Analytics didn&apos;t answer:</b> {data.error}
          {data.service_email && <div className="mt-1 text-xs">Service account: <span className="font-mono" data-testid="ga-service-email">{data.service_email}</span></div>}
          {data.fix_setup && <div className="mt-3 border-t border-rose-300 pt-3" data-testid="ga-fix-steps"><GaSteps /></div>}
        </div>
      )}
      {data?.configured && <GaTagStatus tags={data.tags} />}
      {ready && (
        <>
          <GaTotals t={data.totals} />
          <GaDailyChart rows={data.daily} />
          <GaTables data={data} />
          <p className="text-[11px] text-zinc-500" data-testid="ga-footnote">
            Thank-you views count every view of {data.thank_you_path}. Updated {new Date(data.fetched_at).toLocaleString()} (refreshes every 10 minutes).
          </p>
        </>
      )}
    </div>
  );
}

/** In-app GA4 stats for internal admins; the Google key stays on the server. */
export default function AdminGoogleAnalytics() {
  const { user } = useAuth();
  if (user?.org_type !== "internal" || !["super_admin", "admin", "employee"].includes(user?.role)) {
    return <div className="p-8 text-sm" data-testid="ga-denied">Only your team can see Google Analytics.</div>;
  }
  return <GaBody />;
}
